import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { verifyAdminStatus, refreshAdminStatus, invalidateAdminCache } from '../services/adminVerification';

/**
 * useAdminStatus Hook
 * Provides admin status verification with automatic refresh and caching
 * 
 * @returns {Object} { isAdmin: boolean, isLoading: boolean, error: string | null, refresh: function, role: string }
 */
export function useAdminStatus() {
  const { user, profile } = useAuth();
  
  // Initialize with profile data optimistically (if available)
  const initialIsAdmin = profile?.role === 'admin' || false;
  const initialRole = profile?.role || 'user';
  
  const [isAdmin, setIsAdmin] = useState(initialIsAdmin);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(initialRole);
  
  // Track if we're currently verifying to prevent duplicate requests
  const isVerifyingRef = useRef(false);
  // Track the last verification time
  const lastVerificationRef = useRef(0);
  // Minimum time between verifications (debounce)
  const DEBOUNCE_MS = 1000; // 1 second

  /**
   * Verify admin status from database
   */
  const verify = useCallback(async (forceRefresh = false) => {
    if (!user?.id) {
      setIsAdmin(false);
      setIsLoading(false);
      setError(null);
      setRole(null);
      return;
    }

    // Debounce rapid requests
    const now = Date.now();
    if (!forceRefresh && now - lastVerificationRef.current < DEBOUNCE_MS) {
      return;
    }

    // Prevent duplicate simultaneous requests
    if (isVerifyingRef.current && !forceRefresh) {
      return;
    }

    isVerifyingRef.current = true;
    setIsLoading(true);
    setError(null);
    lastVerificationRef.current = now;

    try {
      const result = forceRefresh
        ? await refreshAdminStatus(user.id)
        : await verifyAdminStatus(user.id);

      if (result.error) {
        setError(result.error);
        // If we have profile data as fallback, use it
        if (profile?.role) {
          const fallbackIsAdmin = profile.role === 'admin';
          setIsAdmin(fallbackIsAdmin);
          setRole(profile.role);
          console.warn('[useAdminStatus] Using fallback profile data due to verification error:', result.error);
        } else {
          setIsAdmin(false);
          setRole(null);
        }
      } else {
        const newIsAdmin = result.isAdmin;
        setIsAdmin(newIsAdmin);
        setRole(result.role || 'user');
        setError(null);
        
        // Log status change
        if (import.meta.env.DEV || newIsAdmin) {
          console.log(`[useAdminStatus] Admin status verified: ${newIsAdmin ? '✅ ADMIN' : '❌ NOT ADMIN'}`, {
            userId: user.id,
            role: result.role,
            fromCache: result.fromCache,
          });
        }
      }
    } catch (err) {
      console.error('[useAdminStatus] Error verifying admin status:', err);
      setError(err.message || 'Failed to verify admin status');
      
      // Fallback to profile data if available
      if (profile?.role) {
        const fallbackIsAdmin = profile.role === 'admin';
        setIsAdmin(fallbackIsAdmin);
        setRole(profile.role);
        console.warn('[useAdminStatus] Using fallback profile data due to error');
      } else {
        setIsAdmin(false);
        setRole(null);
      }
    } finally {
      setIsLoading(false);
      isVerifyingRef.current = false;
    }
  }, [user?.id, profile?.role]);

  /**
   * Manual refresh function
   */
  const refresh = useCallback(() => {
    if (!user?.id) return;
    invalidateAdminCache(user.id);
    verify(true);
  }, [user?.id, verify]);

  // Initial verification on mount and when user/profile changes
  useEffect(() => {
    verify();
  }, [verify]);

  // Use profile data optimistically while verification is happening
  useEffect(() => {
    if (profile?.role) {
      const profileIsAdmin = profile.role === 'admin';
      
      // Optimistically update admin status from profile (immediate UI update)
      if (profileIsAdmin !== isAdmin && isLoading) {
        console.log('[useAdminStatus] Using profile data optimistically:', profileIsAdmin);
        setIsAdmin(profileIsAdmin);
      }
      
      // Update role from profile immediately
      setRole(profile.role);
      
      // If profile says admin but verified status says no (after loading), trigger refresh
      if (profileIsAdmin && !isAdmin && !isLoading) {
        console.log('[useAdminStatus] Profile indicates admin but verification says no, refreshing...');
        refresh();
      }
    }
  }, [profile?.role, isAdmin, isLoading, refresh]);

  // Add visibility change listener to refresh when tab becomes active
  useEffect(() => {
    if (!user?.id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Skip refresh if admin focus check is temporarily disabled (e.g., during file dialog)
        if (window.__adminFocusCheckDisabled) {
          console.log('[useAdminStatus] Skipping visibility refresh - file dialog active');
          return;
        }
        console.log('[useAdminStatus] Tab became visible, refreshing admin status...');
        refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, refresh]);

  // Add focus event listener to refresh when window regains focus
  useEffect(() => {
    if (!user?.id) return;

    const handleFocus = () => {
      // Skip refresh if admin focus check is temporarily disabled (e.g., during file dialog)
      if (window.__adminFocusCheckDisabled) {
        console.log('[useAdminStatus] Skipping focus refresh - file dialog active');
        return;
      }
      console.log('[useAdminStatus] Window regained focus, refreshing admin status...');
      refresh();
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id, refresh]);

  return {
    isAdmin,
    isLoading,
    error,
    refresh,
    role: role || profile?.role || 'user',
  };
}

export default useAdminStatus;
